import { useState, useEffect, SyntheticEvent } from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Box,
  Checkbox,
  UnorderedList,
  ListItem,
  useToast,
  useDisclosure,
  Center,
} from "@chakra-ui/react";
import { Connector, useConnect } from "wagmi";

// Types
import Props from "./WalletMenuProps.type";

// Components
import Alert from "components/Alert";

// Constants
import { TERMS_URL, WALLET_FAQ_URL } from "constants/constants";
import {
  ACTIVATE_SUCCESS,
  CLICK_FAQ_HOW_TO,
  CLICK_TNC_LINK,
  HANDLE_ACTIVATE,
  TNC_ACCEPT,
} from "constants/analytics";

// Utils
import { openLinkInNewTab } from "utils/common";
import { checkOverridingInjection } from "./utils";

// Hooks
import useApp from "hooks/useApp";
import {
  walletIconMapping,
  walletLabelMapping,
} from "constants/walletMenuOptions";

// Images
import metamaskIcon from "assets/images/mm.png";

export const WalletConnectModal = ({ isOpen }: Props) => {
  const { track } = useApp();
  const router = useRouter();
  const toast = useToast();

  const { connect, connectors } = useConnect({
    onError(error: any) {
      let errorMsg = "";

      if (error.message.toString().startsWith("invalid address")) {
        errorMsg =
          "Looks like you are not connected to BSC chain. Please switch the network in the Wallet.";
      }

      toast({
        description: errorMsg || error.message,
        status: "error",
        duration: 5000,
        position: "top",
        isClosable: true,
      });
    },
    onSuccess(data) {
      track(ACTIVATE_SUCCESS);
      closeModal();

      if (data.connector?.id === "walletConnect") {
        router.reload();
      }
    },
  });

  const filteredConnectors = connectors.filter(
    (connector) => connector.id !== "ledgerLive"
  );

  const { closeModal } = useApp();

  const { isOpen: isHowToOpen, onOpen, onClose } = useDisclosure();

  const [isChecked, setChecked] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setChecked(false);
    }
  }, [isOpen]);

  const toggle = () => {
    if (isChecked === true) {
      track(TNC_ACCEPT);
    }
    setChecked((prev) => !prev);
  };

  const handleClose = () => {
    setChecked(false);
    closeModal();
  };

  const handleClick = (e: SyntheticEvent, connector: Connector) => {
    e.stopPropagation();
    track(HANDLE_ACTIVATE);
    connect({ connector });
  };

  const handleTNCClick = () => {
    track(CLICK_TNC_LINK);
    openLinkInNewTab(TERMS_URL);
  };

  const overridingInjections = checkOverridingInjection();

  const renderContent = () => {
    if (isHowToOpen) {
      return (
        <ModalContent w="394px" bg="brand.backgroundLight" m="1rem">
          <ModalCloseButton
            _focus={{
              boxShadow: "none",
            }}
          />
          <ModalBody py="2rem">
            <Center flexDir="column" gap="1.5rem">
              <Box borderRadius="50%" p="1rem" borderWidth={1}>
                <Image
                  src={metamaskIcon}
                  alt={"metamsk logo"}
                  width={50}
                  height={48}
                />
              </Box>
              <Text
                fontFamily="semi-bold"
                fontSize={32}
                textAlign="center"
                lineHeight="38px"
              >
                How to enable MetaMask?
              </Text>
              <Text
                textAlign="center"
                color="brand.textInactive"
                fontFamily="medium"
                lineHeight="24px"
              >
                To enable Metamask, please disable{" "}
                {new Intl.ListFormat().format(overridingInjections)} extension
                from your browser. To know more refer to{" "}
                <Text
                  as="a"
                  href={WALLET_FAQ_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => track(CLICK_FAQ_HOW_TO)}
                  textDecor="underline"
                >
                  FAQs
                </Text>
              </Text>
            </Center>
          </ModalBody>
        </ModalContent>
      );
    }

    return (
      <ModalContent
        w="394px"
        bg="brand.backgroundLight"
        m={{ base: "3rem 0.5rem", lg: "3rem" }}
        opacity={isHowToOpen ? 0 : 1}
      >
        <ModalHeader fontSize="1rem">Select Wallet</ModalHeader>
        <ModalCloseButton
          _focus={{
            boxShadow: "none",
          }}
        />
        <ModalBody p="1rem">
          <Box
            bg="brand.textBg"
            p={{ base: "0.5rem", lg: "1rem" }}
            marginBottom="1rem"
            borderRadius="0.5rem"
          >
            <Checkbox onChange={toggle} isChecked={isChecked}>
              <Text fontSize="12px">
                I have read and accept{" "}
                <Text
                  as="span"
                  color="brand.primary"
                  fontFamily="semi-bold"
                  borderBottom="1px dashed"
                  borderColor="brand.primary"
                  onClick={handleTNCClick}
                >
                  Terms of Service.
                </Text>
              </Text>
            </Checkbox>
          </Box>
          <Alert type="info" withIcon={false} my="1rem">
            <UnorderedList spacing={3} fontSize="12px" px="0.5rem">
              <ListItem>
                For Wallet Connect users, please switch network in the wallet
                app first and then connect for best experience.
              </ListItem>
            </UnorderedList>
          </Alert>
          <Flex
            justify="center"
            gap={{ base: "0.5rem", xl: "1rem" }}
            mb="1rem"
            flexWrap="wrap"
            pointerEvents={!isChecked ? "none" : "inherit"}
            opacity={isChecked ? "1" : "0.5"}
          >
            {filteredConnectors.map((connector: any) => {
              const { id } = connector;

              const isMetamask = id.toLowerCase() === "metamask";

              return (
                <Flex
                  flexDir="column"
                  key={id}
                  pos="relative"
                  justify="center"
                  borderRadius={8}
                  bg="brand.textBg"
                  w={{ base: "145px", xl: "165px" }}
                  outline="none"
                  p="0.5rem 1rem"
                  h="52px"
                  cursor="pointer"
                  onClick={(e) =>
                    isMetamask && overridingInjections.length > 0
                      ? onOpen()
                      : handleClick(e, connector)
                  }
                  _dark={{
                    color: "brand.textColor",
                    _hover: {
                      color: "black",
                    },
                  }}
                  _hover={{
                    background:
                      isMetamask && overridingInjections.length > 0
                        ? "brand.textBg"
                        : "brand.primary",
                    color: "black",
                  }}
                >
                  <Box
                    opacity={
                      isMetamask && overridingInjections.length > 0 ? 0.5 : 1
                    }
                  >
                    <Flex width="100%" gap="0.5rem" alignItems="center">
                      <Box height="20px" overflow="hidden" borderRadius="50%">
                        <Image
                          src={walletIconMapping[id.toLowerCase()]}
                          alt={`${id} Logo`}
                          width={20}
                          height={20}
                        />
                      </Box>
                      <Text
                        fontSize="12px"
                        lineHeight="16px"
                        fontFamily="regular"
                      >
                        {walletLabelMapping[id.toLowerCase()]}
                      </Text>
                    </Flex>
                  </Box>
                  {isMetamask && overridingInjections.length > 0 && (
                    <Text
                      fontSize={10}
                      color="brand.primary"
                      ml="26px"
                      alignSelf="flex-start"
                      borderBottom="1px dotted"
                      opacity={1}
                      fontFamily="regular"
                    >
                      How to enable?
                    </Text>
                  )}
                </Flex>
              );
            })}
          </Flex>
        </ModalBody>
      </ModalContent>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={isHowToOpen ? onClose : handleClose}>
        <ModalOverlay />
        {renderContent()}
      </Modal>
    </>
  );
};

export default WalletConnectModal;
